import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { lastValueFrom } from 'rxjs';

const SESSION_DURATION_MS = 24 * 60 * 60 * 1000;
const LOCAL_TOKEN_KEY = 'local_token_expires_at';
const SESSION_ID_KEY = 'edi_session_id';
const FORCE_INTERACTIVE_KEY = 'edi_force_interactive_login';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private msalService = inject(MsalService);
  private msalReady = false;
  public endPointUrl = environment.baseApiUrl;
  constructor(private http: HttpClient) { }

  async initialize(): Promise<void> {
    try {
      const result = await this.msalService.instance.handleRedirectPromise();
      if (result?.account) {
        this.msalService.instance.setActiveAccount(result.account);
        this.refreshLocalSession();
        await this.ensureSession();
      } else {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          this.msalService.instance.setActiveAccount(accounts[0]);
          this.refreshLocalSession();
          await this.ensureSession();
        }
      }
    } catch (error) {
    } finally {
      this.clearStuckInteractionStatus();
      this.msalReady = true;
    }
  }

  private clearStuckInteractionStatus(): void {
    try {
      const status = localStorage.getItem('msal.interaction.status');
      if (status === 'interaction_in_progress') {
        localStorage.removeItem('msal.interaction.status');
      }
    } catch {
      const sessionStatus = sessionStorage.getItem('msal.interaction.status');
      if (sessionStatus === 'interaction_in_progress') {
        sessionStorage.removeItem('msal.interaction.status');
      }
    }
  }

  isReady(): boolean {
    return this.msalReady;
  }

  getToken(): string | null {
    return localStorage.getItem('token') || null;
  }

  getMSActiveAccount() {
    try {
      const accounts = this.msalService.instance.getAllAccounts();

      if (accounts && accounts.length > 0) {
        const account = accounts[0];
        this.msalService.instance.setActiveAccount(account);
        return account;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  async isLoggedIn(): Promise<boolean> {
    const expiresAt = sessionStorage.getItem(LOCAL_TOKEN_KEY);
    if (expiresAt) {
      if (Date.now() < Number(expiresAt)) {
        return true;
      }
      sessionStorage.removeItem(LOCAL_TOKEN_KEY);
    }

    const accounts = this.msalService.instance.getAllAccounts();

    if (!accounts.length) {
      return false;
    }

    try {
      await this.msalService.instance.acquireTokenSilent({
        account: accounts[0],
        scopes: ['openid', 'profile']
      });

      this.refreshLocalSession();
      return true;

    } catch {
      return false;
    }
  }

  extendSession(): void {
    const expiresAt = Date.now() + SESSION_DURATION_MS;
    sessionStorage.setItem(LOCAL_TOKEN_KEY, String(expiresAt));
  }

  private refreshLocalSession(): void {
    this.extendSession();
  }

  async getAccessToken(): Promise<string | null> {
    const account = this.getMSActiveAccount();
    if (account) {
      try {
        const response = await this.msalService.instance.acquireTokenSilent({
          account,
          scopes: environment.msalLogInRequest,
        });
        if (response?.accessToken) {
          this.extendSession();
          return response.accessToken;
        }
      } catch {
      }
    }
    return sessionStorage.getItem('local_token') || null;
  }

  getSessionId(): string | null {
    try {
      return sessionStorage.getItem(SESSION_ID_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Establish (or touch) the server-side session for this device. The
   * backend is idempotent: presenting a live session id returns it, so
   * one app load never mints duplicate sessions.
   */
  async ensureSession(): Promise<boolean> {
    if (!this.getMSActiveAccount() && !sessionStorage.getItem('local_token')) {
      return false;
    }
    try {
      const res: any = await lastValueFrom(
        this.http.post(environment.baseApiUrl + 'user/v1/sessions/establish', {})
      );
      if (res?.id) {
        sessionStorage.setItem(SESSION_ID_KEY, res.id);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Called when the server reports SESSION_REVOKED: wipe everything that
   * could silently re-authenticate this device (MSAL cache included) and
   * force the next login to be interactive.
   */
  handleSessionRevoked(): void {
    try {
      Promise.resolve(this.msalService.instance.clearCache()).catch(() => undefined);
    } catch {
      // Intentionally empty: guards against synchronous throws from the method call itself
    }
    try {
      sessionStorage.clear();
    } catch {
      // Intentionally empty: storage may be unavailable
    }
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('userDetails');
    } catch {
      // Intentionally empty: storage may be unavailable
    }
    try {
      sessionStorage.setItem(FORCE_INTERACTIVE_KEY, '1');
    } catch {
      // Intentionally empty: storage may be unavailable
    }
  }

  /** True once when a revocation forced the next login to be interactive. */
  consumeForcedInteractive(): boolean {
    try {
      if (sessionStorage.getItem(FORCE_INTERACTIVE_KEY) === '1') {
        sessionStorage.removeItem(FORCE_INTERACTIVE_KEY);
        return true;
      }
    } catch { }
    return false;
  }

  async refreshLocalToken(): Promise<boolean> {
    const refreshToken = sessionStorage.getItem('refresh_token');
    if (!refreshToken) {
      return false;
    }
    try {
      const result: any = await lastValueFrom(
        this.http.post(environment.baseApiUrl + 'api/token/refresh/', {
          refresh: refreshToken
        })
      );
      if (result?.access) {
        sessionStorage.setItem('local_token', result.access);
        if (result?.refresh) {
          sessionStorage.setItem('refresh_token', result.refresh);
        }
        this.extendSession();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  singOut() {
    this.msalService.instance.clearCache();
    sessionStorage.clear();
    localStorage.removeItem('token');
    localStorage.removeItem('userDetails');
    window.location.href = '/signin';
  }

  registerInitial(body: any) {
    const url = environment.baseApiUrl + 'user/v1/user/register';
    let res = this.http.post(url, body);
    return res;
  }
}
